import { useState } from "react";
import { calculateContribution, type ContributionInputs } from "@winche/shared";
import { Disclosure, Input, Label } from "@winche/ui";
import { Metric, MetricGrid } from "../components/ui";
import { usd } from "../lib/format";

const FIELDS: [keyof ContributionInputs, string][] = [
  ["netMonthlyRevenue", "Monthly net receipts per payer (after tax, fees, refunds)"],
  ["variableMonthlyCost", "Monthly variable cost per payer (AI, infra, email, support)"],
  ["paidMonths", "Average paid months per search episode"],
  ["acquisitionCost", "Acquisition cost per new payer (including sales time)"],
  ["fixedMonthlyCost", "Monthly fixed costs (including owner compensation)"],
  ["freeMonthlyCost", "Total monthly cost of free users"],
];

/**
 * Planning tool, not live data (admin-15): a closed card at the end of
 * Monetization whose results read like the rest of the page (Metric).
 */
export function ContributionPlanner() {
  const [values, setValues] = useState<Record<keyof ContributionInputs, string>>({
    netMonthlyRevenue: "",
    variableMonthlyCost: "",
    paidMonths: "",
    acquisitionCost: "",
    fixedMonthlyCost: "",
    freeMonthlyCost: "",
  });
  const result = Object.values(values).every((v) => v.trim() !== "")
    ? calculateContribution(
        Object.fromEntries(Object.entries(values).map(([k, v]) => [k, Number(v)])) as unknown as ContributionInputs,
      )
    : null;
  return (
    <Disclosure
      title="Plan contribution"
      description="Planning inputs in USD, not live results. Fill every field, explicit zeros included; nothing is saved."
      className="mt-8"
    >
      <p className="mb-4 max-w-[68ch] text-caption text-muted-foreground">
        Steady-state model: it includes acquisition to replace departing customers, excludes speculative Radar income and
        does not model cash timing.
      </p>
      <div className="grid grid-cols-1 gap-3 @2xl:grid-cols-2">
        {FIELDS.map(([key, label]) => (
          <div key={key}>
            <Label htmlFor={`contribution-${key}`} className="mb-1 block">
              {label}
            </Label>
            <Input
              id={`contribution-${key}`}
              type="number"
              inputMode="decimal"
              min={key === "paidMonths" ? 0.01 : 0}
              step="any"
              value={values[key]}
              onChange={(e) => setValues({ ...values, [key]: e.target.value })}
            />
          </div>
        ))}
      </div>
      <div className="mt-4" aria-live="polite">
        {result ? (
          <MetricGrid cols={3}>
            <Metric label="Contribution per paid month (before acquisition)" value={usd(result.monthlyContribution)} />
            <Metric label="Contribution per search episode (after acquisition)" value={usd(result.episodeContribution)} />
            <Metric
              label="Active payers for break-even"
              value={result.breakEvenActivePayers === null ? "No break-even" : result.breakEvenActivePayers.toLocaleString("en-US")}
              tone={result.breakEvenActivePayers === null ? "bad" : "default"}
            />
          </MetricGrid>
        ) : (
          <p className="text-label text-muted-foreground">Enter all six assumptions to calculate contribution.</p>
        )}
      </div>
    </Disclosure>
  );
}
